import { useGetYearlyPersonBalances } from '@hr/api/endpoints/index.ts'
import { useCurrentUser } from '@hr/hooks/useCurrentUser.ts'
import { DateTime } from 'luxon'
import { type Dispatch, type FC, type SetStateAction, useEffect, useMemo, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetCalendarSettings } from '@/api/endpoints/index.ts'
import { calendarAddEvent, calendarDeleteEvent, calendarPatch } from '../../../api/index.js'
import type { PersonJSON, PersonSummaryJSON } from '../../../api/model'
import { Button } from '../../../common/Button.tsx'
import { StyledTextArea } from '../../../common/form/base/styledtextarea.jsx'
import { CustomFloatingLabel } from '../../../common/form/customfloatinglabel.jsx'
import { FormDatePicker } from '../../../common/form/formdatepicker.jsx'
import { FormPersonSelectSupervisor } from '../../../common/form/formpersonselectsupervisor'
import { FormSelect } from '../../../common/form/formselect'
import { FormTextArea } from '../../../common/form/formtextarea'
import { IcdCodesSelect } from '../../../common/form/IcdCodesSelect'
import { RemoveIcon } from '../../../common/icons.jsx'
import Switch from '../../../common/Switch'
import type { Nullable } from '../../../common/types'
import { localized } from '../../../common/utils.jsx'
import { queryClient } from '../../../main.jsx'
import { BalancesTable } from './BalancesTable.tsx'
import { getDefaultFormValues, getFormValues } from './newabscencemodal.constants.js'
import { approverOptions, getApproversForEvent } from './newabscencemodalTyped.constants'
import { UserAvatar } from './useravatar.jsx'

interface NewAbscenceModalFormProps {
	user: Nullable<PersonJSON>
	supervisors?: PersonSummaryJSON[]
	event?: any & { personId?: string; approvers?: Array<{ id: string }> } // temporary any;
	openedByDateClick?: boolean
	setIsOpen: Dispatch<SetStateAction<boolean>>
	abscenceOptions: any
}

export const NewAbscenceModalForm: FC<NewAbscenceModalFormProps> = ({
	user,
	supervisors, // PersonSummaryJSON[] (contains all persons for mapping supervisor person numbers to names)
	event,
	openedByDateClick,
	setIsOpen,
	abscenceOptions,
}) => {
	const { t } = useTranslation()
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)

	const [isOverlapModalOpen, setIsOverlapModalOpen] = useState(false)
	const [overlapMessage, setOverlapMessage] = useState('')

	const supervisorNrMap: { [personNumber: number]: PersonSummaryJSON } = {}
	supervisors?.forEach((person) => {
		if (person.personNumber) supervisorNrMap[person.personNumber] = person
	})

	const isEdit = event?.id
	const isPending = isEdit && event?.status === 'OPEN'
	const isRejected = isEdit && event?.status === 'REJECTED'
	const isDone = isEdit && event?.status === 'DONE' // Paid status
	const formDisabled = isEdit && !isPending
	const eventPaid = isEdit && event?.status === 'DONE'
	const eventApprovers = useMemo(() => (event?.approvers ? event.approvers : []), [event])
	const currentUser = useCurrentUser()
	const approversList = approverOptions(user, currentUser, supervisorNrMap) // user is the eventPerson
	const approversForEvent = getApproversForEvent(eventApprovers, supervisorNrMap)

	const form = useForm({
		defaultValues: {
			...getDefaultFormValues(event, user),
			approvers: isEdit ? approversForEvent : [approversList[0]],
		},
	})

	useEffect(() => {
		if (event && openedByDateClick) {
			Promise.resolve().then(() => {
				form.reset({
					...getDefaultFormValues(event, user),
					approvers: approversForEvent || [],
				})
			})
		}
	}, [event, openedByDateClick, form, user, approversForEvent])

	const causeCode = form.watch('causeCode')
	const approvers = form.watch('approvers')
	const dayHours = form.watch('dayHours')
	const start = form.watch('start')
	const end = form.watch('end')
	const isHours = causeCode && (causeCode.value === 'HOURS' || causeCode.value === 'FLEXI')

	const eventLengthDays = () => {
		if (start && end) {
			const startDate = DateTime.fromJSDate(start)
			const endDate = DateTime.fromJSDate(end)
			const difference = endDate.diff(startDate, ['days']).days
			return difference + 1
		}
		return null
	}

	const submitForm = async (formValues: any /*temporary*/) => {
		if (!(await form.trigger())) return

		try {
			const baseFormValues = getFormValues(formValues, approvers, isSickness)

			const payload = {
				...baseFormValues,
			}

			if (event?.id) {
				await calendarPatch(event.id, payload)
			} else {
				await calendarAddEvent({
					...payload,
					scope: 'PERSON',
				})
			}
			queryClient.invalidateQueries({ queryKey: ['calendarEvents'] }) // refetch data
			setIsOpen(false)
		} catch (error) {
			// @ts-expect-error
			if (error.response?.status === 409 && error.response?.data?.errorKeys?.includes('CALENDAR_EVENT_OVERLAP')) {
				setOverlapMessage(
					t(
						'CalendarEventOverlapMessage',
						'Päivälle on jo merkitty tapahtuma. Poista olemassa oleva tapahtuma ennen uuden lisäämistä.',
					),
				)

				setIsOverlapModalOpen(true)
				return
			}

			console.error('Error saving event', error)
		}
	}

	const handleDelete = () => {
		setIsConfirmationOpen(true)
	}

	const deleteEvent = async () => {
		await calendarDeleteEvent(event.id)
		queryClient.invalidateQueries({ queryKey: ['calendarEvents'] }) // refetch data
		setIsOpen(false)
		setIsConfirmationOpen(false)
	}

	const showRemainingDays = causeCode && causeCode.value === '21P'
	const personId = event?.personId ?? user?.personId?.toString()
	const { data: personBalances } = useGetYearlyPersonBalances(personId, {
		query: { enabled: !!showRemainingDays && !!personId },
	})

	const isSickness = causeCode && (causeCode.value === '1P' || causeCode.value === '1U')

	const longAbsenceAllowedCodes = [
		'1P',
		'3P',
		'4P',
		'5P',
		'13P',
		'20P',
		'1U',
		'3U',
		'4U',
		'5U',
		'10U',
		'13U',
		'15U',
		'16U',
		'17U',
	]

	const showLongAbsence = causeCode && longAbsenceAllowedCodes.includes(causeCode.value)

	const { data: calendarSettings } = useGetCalendarSettings()
	const selfCertEnabled = calendarSettings?.selfCertificationEnabled !== false
	const icdRequired = calendarSettings?.icdCodeRequired !== false

	return (
		<FormProvider {...form}>
			<form onSubmit={form.handleSubmit(submitForm)}>
				<Modal.Body>
					<UserAvatar customImage={user?.otherDetails?.image} customName={user?.name} />
					{isSickness && selfCertEnabled && (
						<Row className="mb-2">
							<Col xs={{ span: 6, offset: 6 }}>
								{/* offset 6 työntää sen oikeaan kohtaan */}
								<Form.Group className="mb-0">
									<Switch
										id="ownAnnouncement"
										label={t('OwnAnnouncement', 'Oma ilmoitus')}
										disabled={formDisabled}
										control={form.control}
									/>
								</Form.Group>
							</Col>
						</Row>
					)}

					<Row className="d-flex gap-0">
						<Col xs={6} className="pr-1">
							<FormSelect
								name="causeCode"
								label={t('Event', 'Tapahtuma')}
								options={abscenceOptions}
								required
								disabled={formDisabled}
							/>
						</Col>

						{isSickness && (
							<Col xs={6} className="pl-1">
								<IcdCodesSelect
									name="icdCode"
									formGroupProps={{ xs: 12 }}
									label={t('ICDCode', 'ICD-koodi')}
									required={icdRequired && !form.watch('ownAnnouncement')}
									disabled={formDisabled}
									customErrorMessage={undefined}
									additionalClassnames={undefined}
								/>
							</Col>
						)}
						{showLongAbsence && (
							<Col xs={6}>
								<Form.Group className="mb-0">
									<Switch
										id="longAbsence"
										label={t('LongAbsence', 'Pitkä poissaolo')}
										disabled={formDisabled}
										control={form.control}
									/>
								</Form.Group>
							</Col>
						)}
					</Row>

					{showRemainingDays && personBalances && <BalancesTable data={personBalances} />}
					<Row className="mt-2">
						<FormDatePicker
							name="start"
							label={t('AbsenceStartDate', 'Alkupäivämäärä')}
							formGroupProps={{ xs: 6 }}
							additionalClassnames="pr-1"
							disabled={formDisabled}
							required
						/>
						<FormDatePicker
							name="end"
							label={t('EndDate', 'Loppupäivämäärä')}
							formGroupProps={{ xs: 6 }}
							additionalClassnames="pl-1"
							disabled={formDisabled}
							required
						/>
					</Row>

					{isHours && (
						<Row className="mt-2">
							<FormTextArea
								name="dayHours"
								label={t('HoursPerDay', 'Tuntia/vrk')}
								formGroupProps={{ xs: 6 }}
								textAreaProps={{ as: 'input', type: 'number' }}
								additionalClassnames={'pr-1'}
								required
							/>
							<Form.Group as={Col} xs={6} className={'d-flex flex-column mb-2 pl-1'}>
								<CustomFloatingLabel label={'Total hours'}>
									<StyledTextArea
										value={
											dayHours && eventLengthDays()
												? localized.numberFormatFraction.format(dayHours * eventLengthDays())
												: ''
										}
										as="input"
										disabled={true}
									/>
								</CustomFloatingLabel>
							</Form.Group>
						</Row>
					)}
					<Row>
						<FormPersonSelectSupervisor
							name="approvers"
							label={t('Approvers', 'Hyväksyjät')}
							customErrorMessage={t('PersonIsMandatory', 'Henkilö on pakollinen')}
							disabled={formDisabled}
							required
							options={approversList}
						/>
						<FormTextArea name="info" label={t('InfoOptional', 'Lisätieto (ei pakollinen)')} disabled={formDisabled} />
					</Row>
				</Modal.Body>
				<Modal.Footer className="d-flex flex-row justify-content-between">
					<Button variant="cancel" onClick={() => setIsOpen(false)}>
						{isDone ? t('Close') : t('Cancel', 'Keskeytä')}
					</Button>
					<div className="d-flex gap-2">
						{(isRejected || isPending) && (
							<Button variant="error" onClick={handleDelete}>
								<RemoveIcon />
								{t('Remove')}
							</Button>
						)}
						{!eventPaid && (
							<Button variant="primary" type="submit" onClick={form.handleSubmit(submitForm)} disabled={formDisabled}>
								{t('Submit', 'Tallenna')}
							</Button>
						)}
					</div>
				</Modal.Footer>

				<Modal show={isConfirmationOpen} size="sm" onHide={() => setIsConfirmationOpen(false)} centered>
					<Modal.Header closeButton>
						<Modal.Title>{t('RemoveEvent')}</Modal.Title>
					</Modal.Header>
					<Modal.Body>{t('RemoveEventConfirmation')}</Modal.Body>
					<Modal.Footer className="d-flex flex-row justify-content-between">
						<Button variant="cancel" onClick={() => setIsConfirmationOpen(false)}>
							{t('Cancel', 'Keskeytä')}
						</Button>
						<Button variant="primary" onClick={deleteEvent}>
							<RemoveIcon />
							{t('RemoveEvent')}
						</Button>
					</Modal.Footer>
				</Modal>

				<Modal show={isOverlapModalOpen} size="sm" onHide={() => setIsOverlapModalOpen(false)} centered>
					<Modal.Header closeButton>
						<Modal.Title>{t('CalendarEventOverlapTitle', 'Päällekkäinen tapahtuma')}</Modal.Title>
					</Modal.Header>

					<Modal.Body>{overlapMessage}</Modal.Body>

					<Modal.Footer className="d-flex flex-row justify-content-between">
						<Button variant="cancel" onClick={() => setIsOverlapModalOpen(false)}>
							{t('Close', 'Sulje')}
						</Button>
					</Modal.Footer>
				</Modal>
			</form>
		</FormProvider>
	)
}
